import { Button, Modal, Result, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { MAX_TABS } from './tabRegistry';

const { Paragraph } = Typography;

type Props = {
  /**
   * `denied` — refused on arrival, nothing rendered behind it, so this replaces the page.
   * `evicted` — was admitted and lost its slot; the app is still mounted behind this and may
   * be holding advisor answers that exist nowhere else, so it must overlay rather than replace.
   */
  variant: 'denied' | 'evicted';
  onRetry: () => void;
};

/**
 * Tells the user why this tab is blocked and offers the one action that unblocks it.
 *
 * Deliberately NOT a route and deliberately not `/auth/error`: both would unmount whatever is
 * behind them, and that is destructive here — FormShell holds advisor answers in React memory
 * with no persistence, so navigating an evicted tab away throws out exactly the work this
 * screen is offering to preserve. That is why the notice overlays instead, and why `retry`
 * reclaims the slot in place.
 *
 * The one case that DOES route away is the user returning to an already-evicted tab: AuthGuard
 * sends that to `/auth/error?cause=session-timeout`. So this screen is what an evicted tab
 * shows up to the moment the user comes back to it, and the error page is what they see after.
 *
 * There is no sign-out button either, for a related reason: signing out would delete the
 * session shared by the other tabs, punishing them for this one's arrival. Closing a tab or
 * retrying are the only exits here, and neither touches the session.
 */
export function TabLimitBlocker({ variant, onRetry }: Props) {
  const { t } = useTranslation();

  const retry = (
    <Button type="primary" onClick={onRetry}>
      {t('session.tabLimit.retry')}
    </Button>
  );

  if (variant === 'denied') {
    return (
      <Result
        status="warning"
        title={t('session.tabLimit.title')}
        subTitle={t('session.tabLimit.deniedBody', { max: MAX_TABS })}
        extra={retry}
      />
    );
  }

  return (
    <Modal
      open
      title={t('session.tabLimit.evictedTitle')}
      // Sticky, like the session-expiry warning in useSessionTimer: dismissing this would
      // hand back an app whose session this tab is no longer entitled to refresh.
      closable={false}
      keyboard={false}
      maskClosable={false}
      footer={retry}
    >
      <Paragraph>{t('session.tabLimit.evictedBody', { max: MAX_TABS })}</Paragraph>
    </Modal>
  );
}
